<template>
    <b-form-group :label="theLabel">
        <b-input-group :label="label">
            <b-form-input
                :id="id"
                :ref="id"
                :placeholder="thePlaceholder"
                v-bind="$attrs"
                @focus="onFocus"
                @blur="onBlur"
                v-model="term"
                :value="value"
                :size="size"
                @input="search"
                @keydown.down="down"
                @keydown.up="up"
                @keydown.enter="onEnter(theOptions[index])"
                @keydown.escape="showOptions = false"
                :state="!error ? null : false"
            ></b-form-input>
            <b-input-group-append>
                <b-button :size="size" variant="outline-secondary" @click="onOpenOptions">
                    <i class="fa fa-caret-down"></i>
                </b-button>
            </b-input-group-append>
        </b-input-group>
        <div v-if="!!help && !showOptions">
            <small :class="helpVariant">{{ help }}</small>
        </div>
        <div v-if="!!error && !!errorMsg">
            <small class="text-danger">{{ errorMsg }}</small>
        </div>

        <!-- Dropdown container with scroll indicators -->
        <div class="dropdown-container" v-if="showOptions && theOptions.length > 0" @mouseenter="setActive(true)" @mouseleave="setActive(false)">
            <!-- Top scroll indicator -->
            <div class="scroll-indicator scroll-top" v-if="hasScrollTop">
                <i class="fa fa-chevron-up"></i>
                <span class="indicator-text">Más opciones arriba</span>
            </div>

            <!-- Options list -->
            <div class="items" ref="items-container" @scroll="checkScrollPosition">
                <div
                    class="item"
                    v-for="(item, i) in theOptions"
                    :key="`${id}-${i}`"
                    @click="selectItem(item)"
                    :class="{ 'item-selected': i === index }"
                    @mouseover="() => (index = i)"
                    :ref="`item-${i}`"
                >
                    <div class="item-name">{{ isObject(item) ? item[textField] : item }}</div>
                </div>
            </div>

            <!-- Bottom scroll indicator -->
            <div class="scroll-indicator scroll-bottom" v-if="hasScrollBottom">
                <i class="fa fa-chevron-down"></i>
                <span class="indicator-text">Más opciones abajo</span>
            </div>

            <!-- Results counter -->
            <div class="results-counter" v-if="theOptions.length > 0">{{ theOptions.length }} opciones encontradas</div>
        </div>
    </b-form-group>
</template>

<script>
export default {
    inheritAttrs: false,
    props: {
        placeholder: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            default: () => `select-${parseInt(Math.random() * 1000000)}`,
        },
        resource: {
            type: String,
            default: '',
        },
        options: {
            type: Array,
            default: () => [],
        },
        valueField: {
            type: String,
            default: 'id',
        },
        textField: {
            type: String,
            default: 'name',
        },
        help: {
            type: String,
            default: '',
        },
        helpVariant: {
            type: String,
            default: 'text-muted',
        },
        params: {
            type: Object,
            default: () => ({}),
        },
        hardFilters: {
            type: Object,
            default: () => ({}),
        },
        value: {
            type: [String, Number, Object],
            default: '',
        },
        size: {
            type: String,
            default: 'md',
        },
        customOptions: {
            type: Array,
            default: () => [],
        },
        formater: {
            type: Function,
            default: (item) => item,
        },
        resultFormat: {
            type: String,
            validator: (value) => ['object', 'value'].includes(value),
            default: 'value',
        },
        online: {
            type: Boolean,
            default: false,
        },
        error: {
            type: [String, Boolean],
            default: '',
        },
        errorMsg: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        lodable: {
            type: Boolean,
            default: true,
        },
    },
    data: () => ({
        loadedData: [],
        showOptions: false,
        busy: false,
        term: '',
        selectedItem: null,
        index: 0,
        active: false,
        hasScrollTop: false,
        hasScrollBottom: false,
    }),
    watch: {
        resource() {
            this.fetchData()
        },
        value(val, oldVal) {
            if (!!val) return this.setDefaultValue()
            this.reset()
        },
        theOptions(val) {
            if (val.length === 1) {
                this.index = 0
            }
            this.$nextTick(() => {
                this.checkScrollPosition()
            })
        },
        options(val) {
            if (!val) return
            const item = val.find((item) => (this.isObject(item) ? `${item[this.valueField]}` === `${this.value}` : `${item}` === `${this.value}`))
            this.term = this.isObject(item) ? item[this.textField] : `${item ?? ''}`
        },
        term(val) {
            if (val.length === 0 && this.online) {
                this.fetchData()
                this.reset()
            }
        },
        lodable(val) {
            if (!val) return
            this.fetchData()
        },
        showOptions(val) {
            if (val) {
                this.$nextTick(() => {
                    this.checkScrollPosition()
                })
            }
        },
    },
    computed: {
        theOptions() {
            const items = !!this.resource ? this.loadedData : this.options
            return (
                !!this.term
                    ? items.filter((item) => {
                          const newItem = this.formater(item)
                          const toFilter = this.isObject(newItem) ? newItem[this.textField] : `${newItem}`
                          return clearAccents(toFilter).toLowerCase().includes(clearAccents(this.term.toLowerCase()))
                      })
                    : items
            ).map((item) => this.formater(item))
        },
        thePlaceholder() {
            return this.busy ? 'Cargando...' : this.placeholder
        },
        theLabel() {
            if (!this.label) return ''
            return `${this.label}${this.required ? ' *' : ''}`
        },
    },
    methods: {
        update() {
            if (this.theOptions.length === 0) return
            const selectedItem = this.theOptions.find((item) =>
                this.isObject(item) ? `${item[this.valueField]}` === `${this.value}` : `${item}` === `${this.value}`
            )
            if (!selectedItem) return
            this.selectedItem = selectedItem
            this.term = this.isObject(selectedItem) ? selectedItem[this.textField] : `${selectedItem}`
        },
        isObject(value) {
            return value !== null && typeof value === 'object' && !Array.isArray(value)
        },
        async fetchData() {
            if (!this.lodable) return
            try {
                this.busy = true
                const params = { ...this.hardFilters, ...this.params, term: this.term, filter: 1 }
                const { data } = await this.$repo.get(this.resource, params)
                this.loadedData = this.customOptions.concat(data)
                if (!!this.value) {
                    const item = this.loadedData.find((item) =>
                        this.isObject(item) ? `${item[this.valueField]}` === `${this.value}` : `${item}` === `${this.value}`
                    )
                    if (!item) return

                    this.selectItem(typeof this.formater === 'function' ? this.formater(item) : item)
                }
            } catch (error) {
                console.log({ error })
            } finally {
                this.busy = false
            }
        },
        syncOptions() {
            this.loadedData = this.options
        },
        setActive(value) {
            this.active = value
        },
        selectItem(item) {
            this.selectedItem = item
            this.term = this.isObject(item) ? item[this.textField] : `${item}`
            const result = this.resultFormat === 'object' ? item : this.isObject(item) ? item[this.valueField] : item
            this.$emit('input', result)
            this.$emit('change', result)
            this.$emit('selected', result)
            this.$refs[this.id].blur()
            this.showOptions = false
            this.active = false
        },
        onFocus() {
            this.showOptions = true
            this.$emit('focus')
        },
        onBlur() {
            if (this.active) return
            setTimeout(() => {
                this.showOptions = false
            }, 200)
            this.$emit('blur')
        },
        onOpenOptions() {
            setTimeout(() => {
                this.term = ''
                this.$refs[this.id].focus()
                this.showOptions = true
                // if (this.online) {
                //     this.fetchData()
                // }
            }, 150)
            this.reset()
        },
        reset() {
            this.term = ''
            this.selectedItem = null
            this.$emit('input', '')
            this.$emit('change', '')
            this.$emit('selected', '')
        },
        down() {
            this.index = Math.min(this.index + 1, this.theOptions.length - 1)
            this.scrollToSelected()
        },
        up() {
            this.index = Math.max(this.index - 1, 0)
            this.scrollToSelected()
        },
        scrollToSelected() {
            if (!this.$refs['items-container'] || !this.$refs[`item-${this.index}`]) return
            const container = this.$refs['items-container']
            const element = this.$refs[`item-${this.index}`][0]

            const containerRect = container.getBoundingClientRect()
            const elementRect = element.getBoundingClientRect()

            if (elementRect.bottom > containerRect.bottom) {
                container.scrollTop = element.offsetTop - container.offsetTop - container.clientHeight + element.clientHeight + 8
            } else if (elementRect.top < containerRect.top) {
                container.scrollTop = element.offsetTop - container.offsetTop - 8
            }

            this.checkScrollPosition()
        },
        checkScrollPosition() {
            if (!this.$refs['items-container']) return

            const container = this.$refs['items-container']
            const scrollTop = container.scrollTop
            const scrollHeight = container.scrollHeight
            const clientHeight = container.clientHeight

            this.hasScrollTop = scrollTop > 10
            this.hasScrollBottom = scrollTop + clientHeight < scrollHeight - 10
        },
        scrollTo(position) {
            if (!this.$refs['items-container']) return

            const container = this.$refs['items-container']

            if (position === 'top') {
                // Scroll up by a page
                container.scrollTop -= container.clientHeight - 40
            } else if (position === 'bottom') {
                // Scroll down by a page
                container.scrollTop += container.clientHeight - 40
            }

            this.checkScrollPosition()
        },
        onEnter(item) {
            if (!item) return
            this.selectItem(item)
            const event = new KeyboardEvent('keyup', { key: 'Enter' })
            this.$refs[this.id].$el.dispatchEvent(event)
        },
        search() {
            if (this.online && this.term.length > 1) {
                this.fetchData()
            }
            return this.$emit('input', undefined)
        },
        forwardEvent(eventName, event) {
            if (eventName in this.$listeners) {
                this.$emit(eventName, event)
            }
        },
        setDefaultValue() {
            if (!this.value) return

            const items = !!this.resource ? this.loadedData : this.options
            let item = items
                .map((item) => this.formater(item))
                .find((item) => {
                    const itemIndex = this.isObject(item) ? `${item[this.textField]}` : `${item}`
                    const valueIndex = this.isObject(this.value) ? `${this.value[this.textField]}` : `${this.value}`
                    return itemIndex === valueIndex
                })
            if (!item) return
            this.term = this.isObject(item) ? item[this.textField] : `${item}`
        },
    },
    async mounted() {
        if (!!this.resource) {
            await this.fetchData()
        }
        this.update()
        this.setDefaultValue()
    },
}

function clearAccents(input) {
    input = `${input}`
    var accents = 'ÀÁÂÃÄÅàáâãäåÒÓÔÕÖØòóôõöøÈÉÊËèéêëÇçÌÍÎÏìíîïÙÚÛÜùúûüÑñ'
    var accentless = 'AAAAAAaaaaaaOOOOOOooooooEEEEeeeeCcIIIIiiiiUUUUuuuuNn'
    var strAccentsOut = new String(input)

    for (var y = 0; y < accents.length; y++) {
        for (var z = 0; z < input.length; z++) {
            if (input.charAt(z) == accents.substring(y, y + 1)) {
                strAccentsOut = strAccentsOut.replace(input.charAt(z), accentless.substring(y, y + 1))
            }
        }
    }

    return strAccentsOut
}
</script>
<style scoped>
.dropdown-container {
    position: absolute;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    width: 100%;
    max-height: 400px;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    animation: slideDown 0.2s ease;
}

.items {
    max-height: 300px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;
}

.items::-webkit-scrollbar {
    width: 6px;
}

.items::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.items::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

.item {
    padding: 10px 15px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-left: 3px solid transparent;
}

.item:not(:last-of-type) {
    border-bottom: 1px solid #f0f0f0;
}

.item:hover {
    background: #f5f8ff;
    border-left-color: #ccdcff;
}

.item-selected {
    background: #ebf2ff;
    border-left-color: #4e7dff;
}

.muted {
    color: #6c757d;
}

.scroll-indicator {
    text-align: center;
    padding: 8px;
    background: rgba(255, 255, 255, 0.95);
    font-size: 0.9rem;
    color: #4e7dff;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition: background-color 0.2s ease;
}

.scroll-indicator:hover {
    background: #f0f5ff;
}

.scroll-top {
    border-bottom: 1px solid #eee;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.scroll-bottom {
    border-top: 1px solid #eee;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

.results-counter {
    text-align: center;
    padding: 6px;
    font-size: 0.8rem;
    color: #888;
    background: #f9f9f9;
    border-top: 1px solid #eee;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
